import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  DynamicReport,
  DynamicReportGroupValue,
  DynamicReportSortingValue,
} from '../models/dynamic-report';
import { groupBy } from '../utils/common-utils';
import CubitTable from './cubit-table/cubit-table';
import { CubitTableColumn } from './cubit-table/cubit-table.types';
import { useTranslation } from 'react-i18next';
import { filterOptionsText } from './config-view/config-helper';

const useStyles = makeStyles((theme: Theme) => ({
  sortingLabels: {
    textAlign: 'left',
    paddingLeft: '17px',
  },
  sortingLabel: {
    padding: '4px 8px',
    background: '#ccc',
    borderRadius: '5px',
    margin: '6px 6px 6px 0',
    display: 'inline-block',
    cursor: 'pointer',
    color: '#385690',
    fontWeight: 'bold',
  },
  labelEnabled: {
    background: '#ccffdd',
    border: '1px solid black',
  },
  table: {
    margin: '0 16px',
  },
}));

type CountReportRow = {
  columnValues: string[];
  groupValues: DynamicReportGroupValue[];
};

export const DynamicReportTable: React.FC<{ reportGroup: DynamicReport }> = (
  props
) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const getFilterOptionsText = useMemo(() => filterOptionsText(t), [t]);

  const report = props.reportGroup;
  const [enabledLabels, setEnabledLabels] = useState<string[]>([]);
  const [shownRows, setShownRows] = useState<CountReportRow[]>([]);
  const [showTotals, setShowTotals] = useState<boolean>(true);

  const getChildrenColumns = useCallback(
    (sortingValues: DynamicReportSortingValue[]) => {
      const shownColumns =
        enabledLabels.length === 0 ? report.sortingLabels : enabledLabels;
      return shownColumns.map((x) => {
        const val = sortingValues.find((v) => v.label === x);
        return val?.value ? getFilterOptionsText(val.value) : '-';
      });
    },
    [enabledLabels, report.sortingLabels, getFilterOptionsText]
  );

  useEffect(() => {
    if (
      report.children.length > 0 &&
      report.children[0].groupValues.length > 0
    ) {
      const groupsMap = groupBy<DynamicReport>(report.children, (x) =>
        getChildrenColumns(x.sortingValues).join(',')
      );
      const groups = Array.from(groupsMap.values());
      const newRows = groups.map((group) => {
        let grouped: DynamicReportGroupValue[] = [];
        const groupedMap = groupBy<DynamicReportGroupValue>(
          group.flatMap((m) => m.groupValues),
          (x: DynamicReportGroupValue) => x.label
        );
        Array.from(groupedMap.values()).forEach((m) => {
          grouped.push({
            label: m[0].label,
            count: m.reduce((a, b) => a + b.count, 0),
          });
        });

        return {
          columnValues: getChildrenColumns(group[0].sortingValues),
          groupValues: grouped,
        } as CountReportRow;
      });
      setShownRows(newRows);
    } else {
      const newRows = report.children.map((row) => {
        return {
          columnValues: getChildrenColumns(row.sortingValues),
          groupValues: [],
        } as CountReportRow;
      });
      setShownRows(newRows);
    }
  }, [report, getChildrenColumns]);

  useEffect(() => {
    const isShowTotals =
      report.children.length > 0 && report.children[0].groupValues.length > 0;
    setShowTotals(isShowTotals);
  }, [report.children]);

  const shownColumns = useMemo(
    () => (enabledLabels.length === 0 ? report.sortingLabels : enabledLabels),
    [enabledLabels, report.sortingLabels]
  );

  const columns: CubitTableColumn[] = useMemo(() => {
    let cols = [
      ...shownColumns.map((x) => ({
        headerLabel: x,
        key: x,
      })),
    ];

    if (
      showTotals &&
      report.children.length > 0 &&
      report.children[0]?.groupValues
    ) {
      cols = [
        ...cols,
        ...report.children[0].groupValues.map((x) => ({
          headerLabel: x.label,
          key: x.label,
        })),
      ];
    }
    return cols;
  }, [shownColumns, showTotals, report.children]);

  const tableData = useMemo(
    () =>
      shownRows.map((row) => {
        const rowObj = {};
        shownColumns.forEach((col, index) => {
          rowObj[col] = row.columnValues[index];
        });
        row.groupValues.forEach((g) => (rowObj[g.label] = g.count));
        return rowObj;
      }),
    [shownRows, shownColumns]
  );

  return (
    <>
      {columns.length > 0 && (
        <CubitTable
          sorting
          paging
          columns={columns}
          data={tableData}
          name='dynamic-report'
          className={styles.table}
        />
      )}
    </>
  );
};
